import { useState } from 'react';
import { TextField, Button } from "@mui/material";
import { toast } from 'react-toastify';

import axios from 'axios';
import config from '../../config.js';


// Reusable TextField styling (matching parent component)
const TEXT_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': { borderColor: '#445485' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#445485' },
};

const CreateAmenity = ({ selectedProperty, onAmenityCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateAmenity = async () => {
    if (!formData.name.trim() || !selectedProperty?.value) {
      alert('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      // Create the amenity
      const amenityPayload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        property: selectedProperty.value,
        isActive: true
      };

      const amenityResponse = await axios.post(
        `${config.API_URL}/v2/amenity`,
        amenityPayload,
        {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
      );

      const newAmenity = amenityResponse.data;

      // Create default settings for the new amenity
      const defaultSettings = {
        amenity: newAmenity._id,
        allowedRoles: ['manager', 'tenant', 'resident'], // default to resident
        bookingWindowDays: 30,
        allowedDaysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
        allowedTimeSlots: [{ startTime: "08:00", endTime: "20:00" }],
        slotSizeMinutes: 30,
        maxDurationMinutes: 120,
        maxBookingsPerUserPerDay: 1,
        isApprovalRequired: false
      };

      await axios.post(
        `${config.API_URL}/v2/amenity/booking/setting`,
        defaultSettings,
        {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
      );

      toast.success('Amenity created successfully!');
      
      // Reset form
      setFormData({ name: '', description: '' });
      
      // Notify parent component to refresh amenities list
      if (onAmenityCreated) {
        onAmenityCreated(newAmenity);
      }

    } catch (error) {
      console.error('Error creating amenity:', error);
      toast.error('Failed to create amenity. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="text-center pb-6 border-b border-gray-200 mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Create New Amenity</h2>
        <p className="text-gray-600">
          Creating amenity for <span className="font-semibold text-gray-800">{selectedProperty?.label}</span> property
        </p>
      </div>

      <div className="space-y-6">
        {/* Amenity Details Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#445485' }}></span>
            Amenity Details
          </h3>
          
          <div className="space-y-4">
            <div>
              <TextField
                label="Amenity Name *"
                variant="outlined"
                fullWidth
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Swimming Pool, Gym, Conference Room"
                sx={TEXT_FIELD_SX}
              />
            </div>

            <div>
              <TextField
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Brief description of the amenity and any special features"
                sx={TEXT_FIELD_SX}
              />
            </div>

            <div>
              <TextField
                label="Property"
                variant="outlined"
                fullWidth
                value={selectedProperty?.label || ''}
                disabled
                sx={TEXT_FIELD_SX}
              />
              <p className="text-sm text-gray-500 mt-1">
                The amenity will be created for the selected property above
              </p>
            </div>
          </div>
        </div>

        {/* Information Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-800">
                Default booking settings will be automatically created for this amenity. You can modify them later by selecting the amenity from the dropdown.
              </p>
            </div>
          </div>
        </div>

        {/* Create Button */}
        <div className="flex justify-end pt-6 border-t border-gray-200">
          <Button 
            variant="contained" 
            size="large"
            onClick={handleCreateAmenity}
            disabled={isLoading || !formData.name.trim()}
            sx={{
              backgroundColor: '#C86B2C',
              '&:hover': { backgroundColor: '#b55d26' },
              '&:disabled': { 
                backgroundColor: '#cccccc',
                color: '#666666'
              }
            }}
          >
            {isLoading ? 'Creating...' : 'Create Amenity'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateAmenity;